import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import AdminSidebar from '@/pages/admin/dashboard/components/AdminSidebar';

interface Message {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  language: string;
  status: string;
  created_at: string;
}

export default function AdminMessagesPage() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Message | null>(null);
  const [filter, setFilter] = useState<'all' | 'unread' | 'read' | 'replied'>('all');

  const fetchMessages = async () => {
    let query = supabase.from('contact_messages').select('*').order('created_at', { ascending: false });
    if (filter !== 'all') query = query.eq('status', filter);
    const { data } = await query;
    setMessages(data ?? []);
    setLoading(false);
  };

  useEffect(() => { fetchMessages(); }, [filter]);

  const markStatus = async (id: string, status: string) => {
    await supabase.from('contact_messages').update({ status }).eq('id', id);
    setMessages(prev => prev.map(m => m.id === id ? { ...m, status } : m));
    if (selected?.id === id) setSelected(prev => prev ? { ...prev, status } : null);
  };

  const statusColor = (s: string) =>
    s === 'unread' ? 'bg-[#3B9EF5]/10 text-[#3B9EF5]' : s === 'replied' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500';

  const filters: Array<'all' | 'unread' | 'read' | 'replied'> = ['all', 'unread', 'read', 'replied'];

  return (
    <div className="flex min-h-screen bg-[#F5F7FA]">
      <AdminSidebar />
      <main className="flex-1 p-8 overflow-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#1B2838]">Contact Messages</h1>
          <p className="text-gray-500 text-sm mt-0.5">Messages submitted through the contact form</p>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-5">
          {filters.map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium cursor-pointer capitalize transition-colors whitespace-nowrap ${filter === f ? 'bg-[#1B2838] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'}`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="flex gap-5">
          {/* List */}
          <div className="flex-1 bg-white rounded-xl border border-gray-100 overflow-hidden">
            {loading ? (
              <div className="p-12 text-center text-gray-400">
                <div className="w-8 h-8 border-2 border-[#3B9EF5] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                Loading messages...
              </div>
            ) : messages.length === 0 ? (
              <div className="p-12 text-center text-gray-400">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3"><i className="ri-mail-open-line text-3xl"></i></div>
                <p className="text-sm">No messages found</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {messages.map(msg => (
                  <div
                    key={msg.id}
                    onClick={() => { setSelected(msg); if (msg.status === 'unread') markStatus(msg.id, 'read'); }}
                    className={`flex items-start gap-4 px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors ${selected?.id === msg.id ? 'bg-[#3B9EF5]/5' : ''}`}
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1B2838] text-white text-sm font-bold flex-shrink-0">
                      {msg.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`text-sm font-medium ${msg.status === 'unread' ? 'text-[#1B2838]' : 'text-gray-600'}`}>{msg.name}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(msg.status)}`}>{msg.status}</span>
                      </div>
                      <div className="text-xs text-gray-500 truncate">{msg.subject ?? '(No subject)'}</div>
                    </div>
                    <div className="text-xs text-gray-400 whitespace-nowrap">{new Date(msg.created_at).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Detail */}
          {selected && (
            <div className="w-96 bg-white rounded-xl border border-gray-100 p-6 flex-shrink-0">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-bold text-[#1B2838]">{selected.name}</h3>
                  <a href={`mailto:${selected.email}`} className="text-xs text-[#3B9EF5] hover:underline">{selected.email}</a>
                </div>
                <button onClick={() => setSelected(null)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                  <i className="ri-close-line text-gray-400 text-sm"></i>
                </button>
              </div>
              {selected.subject && (
                <div className="text-sm font-semibold text-gray-700 mb-3">{selected.subject}</div>
              )}
              <div className="text-sm text-gray-600 leading-relaxed mb-5 whitespace-pre-wrap">{selected.message}</div>
              <div className="flex items-center gap-2 text-xs text-gray-400 mb-5">
                <i className="ri-time-line"></i>
                {new Date(selected.created_at).toLocaleString()}
                <span className="ml-2 uppercase font-medium">{selected.language}</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => markStatus(selected.id, 'read')} disabled={selected.status === 'read'} className="flex-1 py-2 rounded-full border border-gray-200 text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-50 disabled:opacity-40 whitespace-nowrap">Mark Read</button>
                <button onClick={() => markStatus(selected.id, 'replied')} disabled={selected.status === 'replied'} className="flex-1 py-2 rounded-full bg-[#1B2838] text-white text-xs font-bold cursor-pointer hover:bg-[#2a3a4a] disabled:opacity-40 whitespace-nowrap">Mark Replied</button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
